import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { defineStrings, useStrings } from "../i18n";
import { Icon, type IconName } from "./Icon";

const strings = defineStrings({ busy: "Please wait" }, { busy: "Un instant" });

export type ButtonVariant = "primary" | "secondary" | "ghost";
export type ButtonSize = "medium" | "big";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Leading icon; decorative, the label carries the meaning. */
  readonly icon?: IconName;
  /** Shows a spinner and blocks repeat presses while keeping the label readable. */
  readonly busy?: boolean;
  readonly children: ReactNode;
  readonly ref?: Ref<HTMLButtonElement>;
}

/**
 * The one button. At least 48px tall (52px by default, 64px for `big`), a pill shape,
 * a press that scales down slightly (none with reduced motion).
 */
export function Button(props: ButtonProps) {
  const { variant = "secondary", size = "medium", icon, busy = false, children, ...rest } = props;
  const t = useStrings(strings);
  const className = ["ui-button", `ui-button--${variant}`, `ui-button--${size}`, rest.className]
    .filter((part) => part !== undefined)
    .join(" ");
  return (
    <button
      type="button"
      {...rest}
      className={className}
      aria-busy={busy || undefined}
      aria-disabled={busy || rest.disabled === true || undefined}
      disabled={rest.disabled}
      onClick={busy ? undefined : rest.onClick}
    >
      {busy ? (
        <span className="ui-spinner" role="img" aria-label={t("busy")} />
      ) : (
        icon !== undefined && <Icon name={icon} size={size === "big" ? 26 : 22} />
      )}
      <span className="ui-button__label">{children}</span>
    </button>
  );
}
